import React, { useState, useEffect } from 'react';
import { Card, DatePicker, Table, Space, Spin, Row, Col, Statistic, Radio, Select, Button } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';
import { useIsMobile } from '../hooks/useIsMobile';

const { RangePicker } = DatePicker;

function DailyDashboard() {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [dateType, setDateType] = useState('order');
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(6, 'day'),
    dayjs()
  ]);
  const [platforms, setPlatforms] = useState([]);
  const [teams, setTeams] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSupplier = currentUser.role === 'supplier';

  useEffect(() => {
    loadOptions();
  }, []);

  useEffect(() => {
    loadData();
  }, [dateType]);

  const loadOptions = async () => {
    try {
      const [platRes, teamRes, supRes] = await Promise.all([
        api.get('/platforms'),
        api.get('/teams'),
        api.get('/suppliers')
      ]);
      if (platRes.code === 200) setPlatforms(platRes.data?.list || platRes.data || []);
      if (teamRes.code === 200) setTeams(teamRes.data?.list || teamRes.data || []);
      if (supRes.code === 200) setSuppliers(supRes.data?.list || supRes.data || []);
    } catch (err) {}
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { date_type: dateType };
      if (dateRange && dateRange[0]) params.start_date = dateRange[0].format('YYYY-MM-DD');
      if (dateRange && dateRange[1]) params.end_date = dateRange[1].format('YYYY-MM-DD');
      if (selectedPlatforms.length > 0) params.platform = selectedPlatforms.join(',');
      if (selectedTeams.length > 0) params.team = selectedTeams.join(',');
      if (selectedSuppliers.length > 0) params.supplier_id = selectedSuppliers.join(',');

      const res = await api.get('/orders/daily-stats', { params });
      if (res.code === 200) setData(res.data);
    } catch (err) {}
    setLoading(false);
  };

  const STATUS_COLS = ['已发送授权', '涨粉中', '已涨粉', '需结账'];

  const columns = [
    {
      title: '日期',
      dataIndex: 'date',
      width: 130,
      render: v => dayjs(v).format('YYYY-MM-DD')
    },
    {
      title: '订单数',
      dataIndex: 'count',
      width: 80,
      align: 'right'
    },
    ...STATUS_COLS.map(s => ({ title: s, dataIndex: s, width: 90, align: 'right', render: v => v || 0 }))
  ];

  // 合计行
  const tableRows = data?.rows || [];
  const totalCount = tableRows.reduce((s, r) => s + r.count, 0);
  const statusTotals = {};
  STATUS_COLS.forEach(s => { statusTotals[s] = tableRows.reduce((sum, r) => sum + (r[s] || 0), 0); });

  const filterStyle = isMobile ? { width: '100%', marginBottom: 8 } : { minWidth: 160, marginBottom: 0 };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: 0, marginBottom: 16 }}><CalendarOutlined /> 每日看板</h3>
        <Row gutter={isMobile ? 8 : 16}>
          <Col xs={24} sm={12}>
            <Card style={{ marginBottom: isMobile ? 8 : 0 }}>
              <Statistic
                title="今日下单总数"
                value={data?.fixed?.todayOrder?.count ?? '-'}
                suffix={
                  <span style={{ fontSize: 14, color: '#999' }}>
                    ¥{Number(data?.fixed?.todayOrder?.total_price ?? 0).toFixed(2)}
                  </span>
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card>
              <Statistic
                title="昨日完成总数"
                value={data?.fixed?.yesterdayComplete?.count ?? '-'}
                suffix={
                  <span style={{ fontSize: 14, color: '#999' }}>
                    ¥{Number(data?.fixed?.yesterdayComplete?.total_price ?? 0).toFixed(2)}
                  </span>
                }
              />
            </Card>
          </Col>
        </Row>
      </div>

      <Card size="small"
        title={isMobile ? '筛选条件' : undefined}
        extra={isMobile ? undefined : undefined}
      >
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 16,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center'
        }}>
          <Radio.Group value={dateType} onChange={e => setDateType(e.target.value)} style={isMobile ? { width: '100%' } : {}}>
            <Radio.Button value="order">按下单日期</Radio.Button>
            <Radio.Button value="complete">按完成日期</Radio.Button>
          </Radio.Group>
          <RangePicker
            value={dateRange}
            onChange={v => setDateRange(v)}
            placeholder={['开始日期', '结束日期']}
            style={isMobile ? { width: '100%' } : {}}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="全部平台"
            value={selectedPlatforms}
            onChange={setSelectedPlatforms}
            maxTagCount={1}
            style={filterStyle}
            options={platforms.map(p => ({ label: p.name, value: p.name }))}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="全部小组"
            value={selectedTeams}
            onChange={setSelectedTeams}
            maxTagCount={1}
            style={filterStyle}
            options={teams.map(t => ({ label: t.name, value: t.name }))}
          />
          {!isSupplier && (
            <Select
              mode="multiple"
              allowClear
              placeholder="全部主体"
              value={selectedSuppliers}
              onChange={setSelectedSuppliers}
              maxTagCount={1}
              style={filterStyle}
              options={suppliers.map(s => ({ label: s.name, value: String(s.id) }))}
            />
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button type="primary" onClick={loadData}>查询</Button>
            <Button onClick={() => { setDateRange([dayjs().subtract(6, 'day'), dayjs()]); setSelectedPlatforms([]); setSelectedTeams([]); setSelectedSuppliers([]); setTimeout(loadData, 100); }}>重置</Button>
          </div>
        </div>
        {loading ? (
          <Spin size="large" style={{ display: 'block', margin: '60px auto' }} />
        ) : (
          <Table
            size="small"
            columns={columns}
            dataSource={tableRows.map((r, i) => ({ ...r, key: i }))}
            pagination={false}
            scroll={{ x: 600, y: 500 }}
            summary={() => tableRows.length > 0 ? (
              <Table.Summary.Row style={{ fontWeight: 'bold', background: '#fafafa' }}>
                <Table.Summary.Cell index={0}>合计</Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">{totalCount}</Table.Summary.Cell>
                {STATUS_COLS.map((s, i) => (
                  <Table.Summary.Cell key={s} index={i + 2} align="right">{statusTotals[s]}</Table.Summary.Cell>
                ))}
              </Table.Summary.Row>
            ) : null}
          />
        )}
      </Card>
    </div>
  );
}

export default DailyDashboard;
